import { FormSectionHandler } from '../entity.handler';
import { UserRoleResponse, UserRoleUpdateRequest, Policy } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { EntityUtils } from '../utils';
import * as uuid from 'uuid';

interface EditorValues {
    policies: ListFieldValue<Policy>[];
}

export class InlinePoliciesSectionHandler implements FormSectionHandler<UserRoleResponse, EditorValues> {

    type = 'form';
    icon = 'passport';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.USERROLE.INLINE_POLICIES.${key}`, values);
    }

    applyUpdate(entity: UserRoleResponse, payload: UserRoleUpdateRequest): Promise<UserRoleResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateUserRole(entity.id, payload));
    }

    mapEntityToEditor(entity: UserRoleResponse): Promise<EditorValues> {
        return Promise.resolve({ policies: (entity.inlinePolicies || []).map(x => this.itemToListItem(x)) });
    }

    mapEditorToEntity(entity: UserRoleUpdateRequest, values: EditorValues): Promise<UserRoleUpdateRequest> {
        entity.inlinePolicies = values.policies.map(x => x.data);
        return Promise.resolve(entity);
    }

    itemToListItem(item: Policy): ListFieldValue<Policy> {
        return {
            id: item.id,
            name: item.name,
            icon: 'policy',
            data: item,
        };
    }

    updateOrAddItem(items: ListFieldValue<Policy>[], existingItem: ListFieldValue<Policy>, item: Policy) {

        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.itemToListItem(item);
        } else {
            items.push(this.itemToListItem(item));
        }
    }


    getFields(entity: UserRoleResponse): Promise<FormBuilderField[]> {
        return Promise.resolve([
            {
                id: 'policies', title: this.translate('FIELDS.POLICIES'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showPolicyModal.bind(this), },
                    { id: 'edit', click: this.showPolicyModal.bind(this), },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ]).then(fields => EntityUtils.prepareBulk(!entity, ['policies'], fields));
    }

    showPolicyModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<Policy>): Promise<Policy> {

        const value = item ? {
            id: item.id,
            name: item.name,
            statements: JSON.stringify(item.data.statements, null, 4),
        } : {
                id: uuid.v4(),
                statements: JSON.stringify([{
                    id: 'statement1',
                    effect: 'allow',
                    actions: ['entities:SomeMethod'],
                    resources: ['*'],
                }], null, 4),
            };

        return this.admin.showFormModal({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    { id: 'name', title: this.translate('FIELDS.NAME'), type: 'text', min: 1, max: 20, required: true },
                    {
                        id: 'statements', title: this.translate('FIELDS.STATEMENTS'), type: 'memo', min: 1, max: 2056, required: true,
                        validate: (_f, _values, v) => {
                            try {
                                JSON.parse(v);
                                return undefined;
                            } catch (err) {
                                return err.message;
                            }
                        },
                    },
                ],
            }],
        }, value).then(result => {
            if (result) {

                const updated = {
                    id: result.id,
                    name: result.name,
                    statements: JSON.parse(result.statements),
                };
                this.updateOrAddItem(values.policies, item, updated);
            }
            return null;
        });


    }


}


